import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/api';
import './AdminReportsPage.css';
import NewsDetailModal from './NewsDetailModal';
// import AuthorDetailModal from './AuthorDetailModal'; // Import the new AuthorDetailModal

// Helper function to safely format dates
const formatFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';

    // 1. Handle Firestore Timestamp objects (preferred)
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    // 2. Handle plain objects like { seconds: ..., nanoseconds: ... } or { _seconds: ..., _nanoseconds: ... }
    if (typeof timestamp === 'object' && (timestamp.seconds || timestamp._seconds)) {
        const seconds = timestamp.seconds || timestamp._seconds;
        if (typeof seconds === 'number') {
            const date = new Date(seconds * 1000);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('en-GB', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            }
        }
    }

    // 3. Handle raw JavaScript Date objects or parseable strings/numbers
    try {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        }
    } catch (e) {
        console.error("Error parsing timestamp:", timestamp, e);
    }

    return 'Invalid Date';
};


const AdminReportsPage = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isUserAdmin, setIsUserAdmin] = useState(false);
    const { currentUser, firebaseUser } = useAuth();

    // States for modals
    const [showNewsDetailModal, setShowNewsDetailModal] = useState(false);
    const [selectedNewsItem, setSelectedNewsItem] = useState(null);
    // const [showAuthorDetailModal, setShowAuthorDetailModal] = useState(false);
    // const [selectedAuthorDetails, setSelectedAuthorDetails] = useState(null);
    // const [selectedAuthorId, setSelectedAuthorId] = useState(null); // To pass author ID to block function
    const [reportFilter, setReportFilter] = useState('pending'); // Default to 'pending'
    const [searchTerm, setSearchTerm] = useState('');

    const checkAdminStatus = useCallback(async (uid) => {
        if (!uid) return false;
        try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            return userDoc.exists() && userDoc.data().admin === true;
        } catch (err) {
            console.error("Error checking admin status:", err);
            return false;
        }
    }, []);

    const fetchReports = useCallback(async () => {
        setLoading(true);
        setError('');

        if (!firebaseUser) {
            setError("You must be logged in to view reports.");
            setLoading(false);
            return;
        }

        const adminStatus = await checkAdminStatus(firebaseUser.uid);
        setIsUserAdmin(adminStatus);

        if (!adminStatus) {
            setError("You do not have administrative privileges to view reports.");
            setLoading(false);
            return;
        }

        try {
            const idToken = await firebaseUser.getIdToken(true);
            const response = await apiClient.get('/admin/reports', { // Assuming your backend has an '/admin/reports/all' endpoint
                headers: { Authorization: `Bearer ${idToken}` }
            });

            // Gather all unique reporter and author IDs to fetch from 'users' collection.
            // This is primarily for reporter details and author 'isBlocked' status if a user profile exists.
            const allReporterUserIds = new Set();
            const allAuthorUserIds = new Set(); // Keep authors separate for specific lookup logic

            response.data.forEach(report => {
                // For reporters, we want full details from 'users'
                if (report.reporterId && report.reporterId !== 'anonymous' && report.reporterId !== 'N/A') {
                    allReporterUserIds.add(report.reporterId);
                }
                // For authors, we mainly need isBlocked status, but only if postAuthorId is a real UID
                if (report.postAuthorId && report.postAuthorId !== 'N/A' && report.postAuthorId !== 'unknown_author') {
                    allAuthorUserIds.add(report.postAuthorId);
                }
            });

            let reporterDetailsMap = new Map();
            if (allReporterUserIds.size > 0) {
                const usersRef = collection(db, 'users');
                const reporterDocs = await getDocs(query(usersRef, where('__name__', 'in', Array.from(allReporterUserIds))));
                reporterDocs.forEach(doc => {
                    reporterDetailsMap.set(doc.id, doc.data());
                });
            }

            let authorBlockedStatusMap = new Map(); // Store only blocked status for authors
            if (allAuthorUserIds.size > 0) {
                 const usersRef = collection(db, 'users');
                 const authorDocs = await getDocs(query(usersRef, where('__name__', 'in', Array.from(allAuthorUserIds))));
                 authorDocs.forEach(doc => {
                     authorBlockedStatusMap.set(doc.id, doc.data().isBlocked || false);
                 });
            }


            const reportsWithDetails = response.data.map(report => {
                // REPORTER DETAILS: Prioritize from report snapshot, fallback to 'users' collection
                const reportedById = report.reporterId || 'N/A';
                const reporterProfileFromUsers = reportedById !== 'N/A' ? reporterDetailsMap.get(reportedById) : undefined;
                const reporterName = report.reporterName || reporterProfileFromUsers?.name || reporterProfileFromUsers?.userName || 'N/A';
                const reporterEmail = report.reporterEmail || reporterProfileFromUsers?.email || 'N/A';
                const reporterMobile = report.reporterMobile || reporterProfileFromUsers?.phoneNumber || 'N/A';

                // AUTHOR DETAILS
                const postAuthorIdVal = report.postAuthorId || 'N/A';
                const postAuthorName = report.postAuthorName || 'N/A';
                const postAuthorMobile = report.postAuthorMobile || 'N/A';
                const postAuthorEmail = report.postAuthorEmail || 'N/A';
                const postAuthorIsBlocked = authorBlockedStatusMap.get(postAuthorIdVal) || false;


                return {
                    ...report,
                    reporterId: reportedById,
                    postId: report.postId || 'N/A',
                    postAuthorId: postAuthorIdVal,

                    reporterDetails: {
                        name: reporterName,
                        email: reporterEmail,
                        phoneNumber: reporterMobile,
                        isBlocked: reporterProfileFromUsers?.isBlocked || false
                    },
                    postAuthorDetails: {
                        name: postAuthorName,
                        email: postAuthorEmail,
                        phoneNumber: postAuthorMobile,
                        isBlocked: postAuthorIsBlocked, // Crucial for Block button state
                    },
                };
            });

            setReports(reportsWithDetails);
        } catch (err) {
            console.error("Error fetching admin reports:", err);
            setError(err.response?.data?.message || "Failed to load reports.");
        } finally {
            setLoading(false);
        }
    }, [firebaseUser, checkAdminStatus]);

    useEffect(() => {
        if (firebaseUser) {
            fetchReports();
        } else {
            setLoading(false);
            setError("Please log in to access the admin dashboard.");
        }
    }, [firebaseUser, fetchReports]);


    const handleMarkAsReviewed = async (reportId) => {
        if (!isUserAdmin) { alert("Permission denied."); return; }
        try {
            const idToken = await firebaseUser.getIdToken(true);
            await apiClient.put(`/admin/reports/${reportId}/status`,
                { status: 'reviewed' },
                { headers: { Authorization: `Bearer ${idToken}` } }
            );
            setReports(prev => prev.filter(report => report.id !== reportId));
            alert("Report marked as reviewed.");
        } catch (error) {
            console.error("Error marking report as reviewed:", error);
            alert("Failed to update report status. Check console.");
        }
    };

    // MODIFIED: Fetch details from userNews directly when "View Post Details" is clicked
    const handleViewPostDetails = async (reportItem) => { // Accept the full report item
        if (!isUserAdmin) { alert("Permission denied."); return; }
        const postId = reportItem.postId;
        const postAuthorIdFromReport = reportItem.postAuthorId; // Use this for the base author ID

        if (!postId || postId === 'N/A') {
            alert("Post ID is missing for this report.");
            console.warn("Attempted to view details for a report missing postId.");
            return;
        }

        try {
            const postDoc = await getDoc(doc(db, 'userNews', postId));
            if (postDoc.exists()) {
                const postData = postDoc.data();
                const authorId = postData.userId || postAuthorIdFromReport || 'N/A';

                // Fetch author's current 'isBlocked' status from 'users' collection
                let authorProfileFromUsers = null;
                if (authorId && authorId !== 'N/A' && authorId !== 'unknown_author') {
                    const authorUserDoc = await getDoc(doc(db, 'users', authorId));
                    if (authorUserDoc.exists()) {
                        authorProfileFromUsers = authorUserDoc.data();
                    }
                }

                // Construct authorDetails for the modal, prioritizing userNews, then users collection
                const authorDetailsForModal = {
                    id: authorId,
                    name: postData.userName || authorProfileFromUsers?.name || authorProfileFromUsers?.userName || 'N/A',
                    email: postData.userEmail || authorProfileFromUsers?.email || 'N/A', // Assuming userEmail might be in userNews or users
                    phoneNumber: postData.userContact || authorProfileFromUsers?.phoneNumber || 'N/A',
                    isBlocked: authorProfileFromUsers?.isBlocked || false, // Crucial for modal's block button state
                    createdAt: authorProfileFromUsers?.createdAt, // From users collection if available
                };

                setSelectedNewsItem({
                    id: postId,
                    ...postData,
                    reportId: reportItem.id,
                    authorDetails: authorDetailsForModal // Pass the rich author details
                });
                setShowNewsDetailModal(true);
            } else {
                alert("News post not found in the database.");
            }
        } catch (error) {
            console.error("Error fetching news post and author details for modal:", error);
            alert(`Failed to fetch news post details. Error: ${error.message}.`);
        }
    };
    

    // const handleViewAuthorDetails = async (authorId, currentAuthorDetails) => {
    //     if (!isUserAdmin) { alert("Permission denied."); return; }
    //     if (!authorId || authorId === 'unknown_author' || authorId === 'N/A') {
    //         alert("Author ID is missing or unknown for this report.");
    //         return;
    //     }

    //     try {
    //         // Re-fetch current details to ensure up-to-date status (e.g., if blocked recently)
    //         const authorDoc = await getDoc(doc(db, 'users', authorId));
    //         if (authorDoc.exists()) {
    //             setSelectedAuthorDetails(authorDoc.data());
    //             setSelectedAuthorId(authorId);
    //             setShowAuthorDetailModal(true);
    //         } else {
    //             alert("Author details not found.");
    //         }
    //     } catch (error) {
    //         console.error("Error fetching author details:", error);
    //         alert(`Failed to fetch author information. Error: ${error.message}. See console.`);
    //     }
    // };

     const handleToggleBlockAuthor = async (authorId, shouldBlock) => {
        if (!isUserAdmin) { alert("Permission denied."); return; }
        if (!authorId || authorId === 'N/A' || authorId === 'unknown_author') {
            alert("Author ID is missing or unknown. Cannot perform action.");
            return;
        }
        const actionText = shouldBlock ? "BLOCK" : "UNBLOCK";
        if (window.confirm(`Are you sure you want to ${actionText} author ID: ${authorId}?`)) {
            try {
                const authorDocRef = doc(db, 'users', authorId);
                const authorDoc = await getDoc(authorDocRef);

                if (!authorDoc.exists()) {
                    if (shouldBlock) { // Only create a placeholder if blocking a non-existent user
                        console.warn(`Author ${authorId} not found in 'users' collection. Creating a placeholder entry with blocked status.`);
                        await setDoc(authorDocRef, {
                            name: 'User Placeholder', // Name will be updated to Blocked/Unblocked
                            email: 'N/A',
                            phoneNumber: 'N/A',
                            isBlocked: true, // Initially block
                            createdAt: new Date(),
                        }, { merge: true });
                    } else {
                        alert(`Cannot unblock author ${authorId}. No user profile found in the database.`);
                        return; // Exit if trying to unblock a non-existent user
                    }
                } else {
                    await updateDoc(authorDocRef, { isBlocked: shouldBlock });
                }

                const idToken = await firebaseUser.getIdToken(true);
                // Adjust API endpoint/payload based on your backend for toggle
                await apiClient.post(
                    `/admin/users/${authorId}/toggleBlockStatus`, // Use the new backend endpoint
                    { isBlocked: shouldBlock }, // Send the new desired status in the request body
                    { headers: { Authorization: `Bearer ${idToken}` } }
                );

                alert(`Author ${authorId} has been ${shouldBlock ? 'blocked' : 'unblocked'}.`);
                fetchReports(); // Refresh reports to reflect status
            } catch (error) {
                console.error(`Error ${actionText.toLowerCase()}ing author:`, error);
                alert(`Failed to ${actionText.toLowerCase()} author. See console for details.`);
            }
        }
    };

    const handleDeletePost = async (reportId, postId) => {
        if (!isUserAdmin) { alert("Permission denied."); return; }
        if (!postId || postId === 'N/A') {
            alert("Post ID is missing. Cannot delete.");
            return;
        }
        if (window.confirm(`Are you sure you want to DELETE news post ID: ${postId}? This action is irreversible.`)) {
            try {
                const idToken = await firebaseUser.getIdToken(true);
                await apiClient.delete(`/admin/news/${postId}`, {
                    headers: { Authorization: `Bearer ${idToken}` }
                });
                // Update the report status as well
                await apiClient.put(`/admin/reports/${reportId}/status`,
                    { status: 'action_taken', actionDetails: 'Post deleted' },
                    { headers: { Authorization: `Bearer ${idToken}` } }
                );

                setReports(prev => prev.filter(report => report.id !== reportId));
                alert("News post deleted and report updated.");
                fetchReports(); // Refresh reports
            } catch (error) {
                console.error("Error deleting post:", error);
                alert("Failed to delete post. See console for details.");
            }
        }
    };

    if (loading) return <div className="admin-loader">Loading reports...</div>;
    if (error) return <div className="admin-error-message">{error}</div>;
    if (!isUserAdmin && !loading && !error) return <div className="admin-message">You do not have access to this page.</div>;

    const filteredReports = reports.filter(report => {
        const matchesFilter = 
            reportFilter === 'all' || 
            (reportFilter === 'pending' && report.status === 'pending') ||
            (reportFilter === 'blocked' && report.postAuthorDetails?.isBlocked);

        // Search logic
        const s = searchTerm.toLowerCase();
        const matchesSearch = 
            report.id?.toLowerCase().includes(s) ||
            report.postTitle?.toLowerCase().includes(s) ||
            report.reporterDetails?.name?.toLowerCase().includes(s) ||
            report.postAuthorDetails?.name?.toLowerCase().includes(s);

        return matchesFilter && matchesSearch;
    });

    return (
        <div className="admin-reports-page-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Admin Reports</h2>
            <input 
                    type="text" 
                    placeholder="Search by Title, Author, Reporter or ID..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="admin-search-input"
                    style={{ width: '350px' }}
                />
            </div>

            {/* Filter Buttons */}
            <div className="report-filters">
                <button
                    className={`filter-button ${reportFilter === 'pending' ? 'active' : ''}`}
                    onClick={() => setReportFilter('pending')}
                >
                    Pending Reports ({reports.filter(r => r.status === 'pending').length})
                </button>
                <button
                    className={`filter-button ${reportFilter === 'blocked' ? 'active' : ''}`}
                    onClick={() => setReportFilter('blocked')}
                >
                    Blocked Accounts ({reports.filter(r => r.postAuthorDetails.isBlocked).length})
                </button>
                <button
                    className={`filter-button ${reportFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setReportFilter('all')}
                >
                    All Reports ({reports.length})
                </button>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <colgroup>
                        <col className="col-report-id" />
                        <col className="col-reporter-details" />
                        <col className="col-post-title" />
                        <col className="col-reason" />
                        <col className="col-reported-at" />
                        <col className="col-author-details" />
                        <col className="col-actions" />
                        <col className="col-details-button" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Report ID</th>
                            <th>Reported By</th>
                            <th>Post Title</th>
                            <th>Reason</th>
                            <th>Reported At</th>
                            <th>Author Info</th>
                            <th>Actions</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredReports.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="no-reports-message">No {reportFilter} reports to display.</td>
                            </tr>
                        ) : (
                            filteredReports.map(report => (
                                <tr key={report.id}>
                                    <td>{report.id || 'N/A'}</td>
                                    <td>
                                        <strong>ID:</strong> {report.reporterId || 'N/A'}<br/>
                                        <strong>Name:</strong> {report.reporterDetails.name || 'N/A'}<br/>
                                        <strong>Email:</strong> {report.reporterDetails.email || 'N/A'}<br/>
                                        <strong>Mobile:</strong> {report.reporterDetails.phoneNumber || 'N/A'}
                                    </td>
                                    <td>{report.postTitle || 'N/A'}</td>
                                    <td>
                                        {report.reason || 'N/A'}
                                        {report.reason === 'Other' && report.customText && (
                                            <>
                                                <br/>
                                                <span className="custom-reason-text">({report.customText})</span>
                                            </>
                                        )}
                                    </td>
                                    <td>{formatFirestoreTimestamp(report.reportedAt)}</td>
                                    <td>
                                        <strong>ID:</strong> {report.postAuthorId || 'N/A'}<br/>
                                        <strong>Name:</strong> {report.postAuthorDetails.name || 'N/A'}<br/>
                                        <strong>Email:</strong> {report.postAuthorDetails.email || 'N/A'}<br/>
                                        <strong>Mobile:</strong> {report.postAuthorDetails.phoneNumber || 'N/A'}
                                        {report.postAuthorDetails.isBlocked && (
                                            <span className="status-blocked" style={{marginLeft: '5px', fontSize: '0.8em'}}>(Blocked)</span>
                                        )}
                                    </td>
                                <td className="admin-actions-cell">
                                        <button onClick={() => handleDeletePost(report.id, report.postId)} className="btn-delete" disabled={!report.postId || report.postId === 'N/A'}>Delete Post</button>
                                        <button
                                            onClick={() => handleToggleBlockAuthor(report.postAuthorId, !report.postAuthorDetails.isBlocked)}
                                            className={`btn-toggle-block-author ${report.postAuthorDetails.isBlocked ? 'btn-unblock' : 'btn-block-author'}`}
                                            disabled={
                                                !report.postAuthorId ||
                                                report.postAuthorId === 'N/A' ||
                                                report.postAuthorId === 'unknown_author' // Still disable if ID is completely invalid
                                            }
                                        >
                                            {report.postAuthorDetails.isBlocked ? 'Unblock Author' : 'Block Author'}
                                        </button>
                                        <button onClick={() => handleMarkAsReviewed(report.id)} className="btn-approve">Mark Reviewed</button>
                                    </td>
                                    <td className="admin-details-button-cell">
                                        <button
                                            onClick={() => handleViewPostDetails(report)} // Pass the whole report item
                                            className="btn-details-post"
                                            disabled={!report.postId || report.postId === 'N/A'}
                                            title="View Post & Author Details"
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="1"></circle>
                                                <circle cx="19" cy="12" r="1"></circle>
                                                <circle cx="5" cy="12" r="1"></circle>
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showNewsDetailModal && (
                <NewsDetailModal
                    newsItem={selectedNewsItem} // This newsItem now contains authorDetails correctly
                    onClose={() => setShowNewsDetailModal(false)}
                    // Pass onBlockAuthor directly to NewsDetailModal if you want to block from there
                    onToggleBlockAuthor={handleToggleBlockAuthor}
                />
            )}
        </div>
    );
};

export default AdminReportsPage;